'use client'
import { usePathname, useSearchParams } from 'next/navigation'
import { createContext, useContext, useEffect } from 'react'
import { usePostHog } from 'posthog-js/react'
import posthog from 'posthog-js'
import { PostHogProvider as PHProvider } from 'posthog-js/react'
import { useSession } from '@repo/users'
import { identify, updateProperties } from './index'
import { fetchIsFreemiumStatus } from '../utils/fetch-is-freemium'

export const usePHTracking = () => usePostHog()

export function PostHogProvider({
  children,
  phKey,
  phHost,
  isDebug,
  disableFlagsOnFirstLoad,
}: {
  children: React.ReactNode
  phKey?: string
  phHost?: string
  isDebug?: boolean
  disableFlagsOnFirstLoad?: boolean
}) {
  const { session, isLoading: isSessionLoading } = useSession()
  const currentExamId = session?.user?.opoId
  const examName = session?.user?.opoName
  const academyName = session?.user?.platform
  const academyId = session?.user?.platformId
  const fullName = session?.user?.name
  const permissions = session?.user?.permissions
  const joinDate = session?.user?.joinDate

  useEffect(() => {
    if (phKey) {
      posthog.init(phKey || '', {
        api_host: phHost || 'https://posthog.formacion.ninja',
        loaded: (posthog) => {
          if (isDebug) posthog.debug()
          posthog.register({
            environment: process.env.NEXT_PUBLIC_ENVIROMENT,
          })
        },
        person_profiles: 'always',
        defaults: '2025-05-24',
        // Web vitals aqui tambien: medir solo la beta no sirve de nada si no
        // hay contra que compararla. Es la unica forma de responder a «la
        // version nueva va mas lenta» con un numero.
        capture_performance: { web_vitals: true },
        advanced_disable_feature_flags_on_first_load:
          disableFlagsOnFirstLoad ?? false,
      })
    } else {
      console.error('PH not initialized')
    }
  }, [])

  useEffect(() => {
    if (!isSessionLoading && session?.user) {
      if (session?.user?.email) {
        identify({
          email: session.user.email,
          currentExamId,
          examName,
          academyName,
          academyId,
          fullName,
          joinDate,
        })

        // Check if user is freemium and update properties
        fetchIsFreemiumStatus().then((isFreemium) => {
          posthog.register({
            is_freemium: isFreemium,
            user_type: isFreemium ? 'freemium' : 'paid',
          })
        })
      }

      updateProperties({
        email: session?.user?.email,
        currentExamId,
        examName,
        academyName,
        academyId,
        fullName,
        permissions,
        joinDate,
      })
    }
  }, [session, isSessionLoading, posthog])

  return <PHProvider client={posthog}>{children}</PHProvider>
}
